import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, inr } from "../lib/format";
import { Loading, Empty, SectionTitle } from "../components/kit";
import PeriodBar from "../components/PeriodBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StaffPicker } from "../components/gms/GmsForms";
import { FlowTiles, MtoCounts, MtoTable, MtoHistory } from "../components/mto/MtoPanels";

function StaffTable({ rows }) {
  return (
    <div className="bs-card overflow-x-auto" data-testid="mto-by-staff">
      <table className="w-full text-sm text-left min-w-[720px]">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-4 py-3">Staff</th><th className="px-3 text-right">Placed</th><th className="px-3 text-right">Closed</th><th className="px-3 text-right">Cancelled</th><th className="px-3 text-right">Closed Today</th><th className="px-3 text-right">Cancelled Today</th><th className="px-4 text-right">Outstanding</th></tr>
        </thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-t border-slate-50" data-testid={`mto-staff-row-${r.employee_id}`}>
            <td className="px-4 py-3 font-semibold text-[#011E38]">{r.name}<div className="text-xs text-slate-500 font-normal">{r.employee_id}</div></td>
            <td className="px-3 text-right num">{inr(r.period.placed.value)}</td>
            <td className="px-3 text-right num text-green-700">{inr(r.period.closed.value)}</td>
            <td className="px-3 text-right num text-red-700">{inr(r.period.cancelled.value)}</td>
            <td className="px-3 text-right num">{inr(r.today.closed.value)}</td>
            <td className="px-3 text-right num">{inr(r.today.cancelled.value)}</td>
            <td className="px-4 text-right num font-bold">{inr(r.outstanding.value)} <span className="text-xs text-slate-400 font-normal">({r.outstanding.count})</span></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Closings({ rows, onOpen }) {
  if (!rows.length) return <Empty>No MTO closings or cancellations in this period.</Empty>;
  return (
    <div className="bs-card overflow-x-auto" data-testid="mto-closings">
      <table className="w-full text-sm text-left min-w-[720px]">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-4 py-3">Date</th><th className="px-3">Staff</th><th className="px-3">Customer · MTO</th><th className="px-3 text-right">Original</th><th className="px-3 text-right">Closed</th><th className="px-3 text-right">Cancelled</th><th className="px-4 text-right">Remaining</th></tr>
        </thead>
        <tbody>{rows.map((t) => (
          <tr key={t.id} onClick={() => onOpen(t.mto_id)} className="border-t border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`mto-closing-row-${t.id}`}>
            <td className="px-4 py-3">{fmtDate(t.date)}</td><td className="px-3">{t.staff_name}</td>
            <td className="px-3"><div className="font-semibold text-[#011E38]">{t.customer_name}</div><div className="text-xs text-slate-500">{t.mto_ref}{t.source === "manager_correction" && " · correction"}</div></td>
            <td className="px-3 text-right num">{inr(t.original)}</td>
            <td className="px-3 text-right num text-green-700">{inr(t.closed)}</td>
            <td className="px-3 text-right num text-red-700">{inr(t.cancelled)}</td>
            <td className="px-4 text-right num font-bold">{inr(t.outstanding)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function MtoTracker() {
  const { user, range, storeId, stores, settings } = useAuth();
  const [staffId, setStaffId] = useState("");
  const [store, setStore] = useState(user.role === "manager" ? storeId : "");
  const filters = useMemo(() => Object.fromEntries(Object.entries({ store_id: store, staff_id: staffId }).filter(([, v]) => v)), [store, staffId]);
  const [d, setD] = useState(null);
  const [key, setKey] = useState(0);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!range) return;
    const p = { start: range.start, end: range.end, ...filters };
    Promise.all([api.get("/mto/summary", { params: p }), api.get("/mto/list", { params: filters }), api.get("/mto/closings", { params: p }),
      api.get("/mto/closings", { params: { ...filters, start: settings.effective_date, end: settings.effective_date } })])
      .then(([s, l, c, t]) => setD({ s: s.data, list: l.data, closings: c.data, today: t.data }));
  }, [range, filters, key, settings.effective_date]);

  return (
    <div className="bs-rise">
      <div className="mb-6"><div className="bs-eyebrow">Made to order</div><h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1" data-testid="mto-title">MTO Tracker</h1></div>
      <PeriodBar hideBasis />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="mto-filters">
        {user.role === "admin" && (
          <select value={store} onChange={(e) => { setStore(e.target.value); setStaffId(""); }} className="h-11 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="mto-store-filter">
            <option value="">All stores</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div className="w-56"><StaffPicker value={staffId} onChange={setStaffId} storeId={store} allLabel="All staff" testid="mto-staff-filter" /></div>
      </div>
      {!d ? <Loading /> : (
        <div className="space-y-8">
          <section><SectionTitle eyebrow={fmtDate(d.s.as_of)} title="TODAY'S MTO" /><FlowTiles f={d.s.today} out={d.s.outstanding} prefix="mto-today" /></section>
          <section><SectionTitle eyebrow="Selected period" title="MTO in period" /><FlowTiles f={d.s.period} out={d.s.outstanding} prefix="mto-period" />
            <p className="text-xs text-slate-500 mt-2">Placed / Closed / Cancelled are for the selected dates. Outstanding is the current balance.</p></section>
          <MtoCounts s={d.s} />
          <Tabs defaultValue="staff">
            <TabsList>
              <TabsTrigger value="staff" data-testid="mto-tab-staff">Staff-wise</TabsTrigger>
              <TabsTrigger value="today" data-testid="mto-tab-today">Today's Closing</TabsTrigger>
              <TabsTrigger value="closings" data-testid="mto-tab-closings">Closings in Period</TabsTrigger>
              <TabsTrigger value="open" data-testid="mto-tab-open">Open MTOs</TabsTrigger>
              <TabsTrigger value="all" data-testid="mto-tab-all">All MTOs</TabsTrigger>
            </TabsList>
            <TabsContent value="staff" className="mt-5"><StaffTable rows={d.s.by_staff || []} /></TabsContent>
            <TabsContent value="today" className="mt-5"><Closings rows={d.today} onOpen={setOpen} /></TabsContent>
            <TabsContent value="closings" className="mt-5"><Closings rows={d.closings} onOpen={setOpen} /></TabsContent>
            <TabsContent value="open" className="mt-5"><MtoTable rows={d.list.filter((m) => m.outstanding > 0)} onOpen={setOpen} showStaff testid="mto-open-table" /></TabsContent>
            <TabsContent value="all" className="mt-5"><MtoTable rows={d.list} onOpen={setOpen} showStaff testid="mto-all-table" /></TabsContent>
          </Tabs>
        </div>
      )}
      {open && <MtoHistory id={open} onClose={() => setOpen(null)} onChanged={() => setKey((k) => k + 1)} />}
    </div>
  );
}
